import React, { useEffect, useState, useRef } from "react";
import { Card, Row, Col, Typography, Spin, Table, Tag, Avatar, Badge, Select, Divider } from "antd";
import { useGoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation, Trans } from "react-i18next";
import axios from 'axios';
import { toast } from "react-hot-toast";
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useNavigate, useSearchParams } from "react-router-dom";
import { groupedTools } from '../data/toolsData';
import {
  DollarOutlined,
  ToolOutlined,
  FireOutlined,
  CalendarOutlined,
  GlobalOutlined,
  LogoutOutlined,
  UserOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  DownloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  BarController,
  PieController
} from 'chart.js';
import StandardButton from '../components/StandardButton';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  ChartTitle,
  Tooltip,
  Legend,
  BarController,
  PieController
);

const { Title, Text } = Typography;
const { Option } = Select;

const AnalyticsDashboard = ({ userProfileData, creditBalance, creditUsages, handleLogout, selectedAmount, setSelectedAmount }) => {
  const { t } = useTranslation();
  const barChartRef = useRef(null);
  const pieChartRef = useRef(null);
  const barChartInstance = useRef(null);
  const pieChartInstance = useRef(null);

  // Calculate KPIs from creditUsages
  const calculateKPIs = (usages) => {
    const totalSpent = usages.reduce((sum, usage) => sum + (usage.cost || 0), 0);
    const toolsUsed = new Set(usages.map(u => u.tool_name).filter(Boolean)).size;
    
    // Find most used tool
    const toolCounts = {};
    usages.forEach(usage => {
      if (usage.tool_name) {
        toolCounts[usage.tool_name] = (toolCounts[usage.tool_name] || 0) + 1;
      }
    });
    
    let mostUsedTool = 'N/A';
    if (Object.keys(toolCounts).length > 0) {
      mostUsedTool = Object.keys(toolCounts).reduce((a, b) => 
        toolCounts[a] > toolCounts[b] ? a : b
      );
    }
    
    // Get last used date
    const validDates = usages
      .filter(u => u.created_at)
      .map(u => new Date(u.created_at));
    
    let lastUsed = t('never');
    if (validDates.length > 0) {
      const latestDate = new Date(Math.max(...validDates));
      lastUsed = latestDate.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      });
    }
    
    // Calculate monthly trend
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const lastMonthSpent = usages
      .filter(u => {
        if (!u.created_at) return false;
        const date = new Date(u.created_at);
        return date.getMonth() === (currentMonth === 0 ? 11 : currentMonth - 1) && 
               date.getFullYear() === (currentMonth === 0 ? currentYear - 1 : currentYear);
      })
      .reduce((sum, usage) => sum + (usage.cost || 0), 0);
    
    const thisMonthSpent = usages
      .filter(u => {
        if (!u.created_at) return false;
        const date = new Date(u.created_at);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      })
      .reduce((sum, usage) => sum + (usage.cost || 0), 0);
    
    const monthlyTrend = lastMonthSpent > 0 ? 
      ((thisMonthSpent - lastMonthSpent) / lastMonthSpent * 100).toFixed(1) : 
      thisMonthSpent > 0 ? 100 : 0;
    
    return {
      totalSpent: totalSpent.toFixed(2),
      toolsUsed,
      mostUsedTool,
      lastUsed,
      monthlyTrend: parseFloat(monthlyTrend),
      thisMonthSpent: thisMonthSpent.toFixed(2)
    };
  };

  const kpiData = calculateKPIs(creditUsages || []);

  // Get cost over time data for bar chart
  const getCostOverTimeData = () => {
    const dailyData = {};
    const usages = creditUsages || [];
    
    usages.forEach(usage => {
      if (usage.created_at) {
        const date = new Date(usage.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit'
        });
        
        if (!dailyData[date]) {
          dailyData[date] = 0;
        }
        dailyData[date] += (usage.cost || 0);
      }
    });
    
    const sortedDates = Object.keys(dailyData).sort((a, b) => 
      new Date(a) - new Date(b)
    ).slice(-7); // Last 7 days
    
    return {
      labels: sortedDates,
      data: sortedDates.map(date => dailyData[date])
    };
  };

  // Get tool breakdown data for pie chart
  const getToolBreakdownData = () => {
    const toolCosts = {};
    const usages = creditUsages || [];
    
    usages.forEach(usage => {
      if (usage.tool_name && usage.cost) {
        toolCosts[usage.tool_name] = (toolCosts[usage.tool_name] || 0) + usage.cost;
      }
    });
    
    const sortedTools = Object.keys(toolCosts).sort((a, b) => toolCosts[b] - toolCosts[a]);
    
    const total = Object.values(toolCosts).reduce((a, b) => a + b, 0);
    
    return {
      labels: sortedTools,
      data: sortedTools.map(tool => toolCosts[tool]),
      percentages: sortedTools.map(tool => total > 0 ? ((toolCosts[tool] / total) * 100).toFixed(1) : '0.0'),
      total
    };
  };

  // Initialize charts
  useEffect(() => {
    if (barChartRef.current && pieChartRef.current) {
      // Destroy existing charts
      if (barChartInstance.current) {
        barChartInstance.current.destroy();
      }
      if (pieChartInstance.current) {
        pieChartInstance.current.destroy();
      }

      const costData = getCostOverTimeData();
      const toolData = getToolBreakdownData();

      // Colors for charts
      const barColors = ['#3b82f6', '#1d4ed8', '#60a5fa', '#93c5fd', '#2563eb', '#1e40af', '#3b82f6'];
      const pieColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#84cc16', '#f97316', '#8b5cf6', '#06b6d4'];

      // Create Bar Chart
      if (costData.labels.length > 0) {
        barChartInstance.current = new ChartJS(barChartRef.current, {
          type: 'bar',
          data: {
            labels: costData.labels,
            datasets: [{
              label: t('daily_cost'),
              data: costData.data,
              backgroundColor: barColors,
              borderColor: barColors.map(color => color.replace('0.8', '1')),
              borderWidth: 1,
              borderRadius: 6,
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleFont: { size: 12 },
                bodyFont: { size: 13 },
                callbacks: {
                  label: function(context) {
                    return `$${context.raw.toFixed(2)}`;
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  display: false
                }
              },
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return '$' + value;
                  }
                },
                grid: {
                  color: 'rgba(0, 0, 0, 0.05)'
                }
              }
            }
          }
        });
      }

      // Create Pie Chart
      if (toolData.labels.length > 0) {
        pieChartInstance.current = new ChartJS(pieChartRef.current, {
          type: 'pie',
          data: {
            labels: toolData.labels,
            datasets: [{
              data: toolData.data,
              backgroundColor: pieColors,
              borderColor: '#fff',
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'right',
                labels: {
                  padding: 15,
                  usePointStyle: true,
                  pointStyle: 'circle',
                  font: {
                    size: 11
                  }
                }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleFont: { size: 12 },
                bodyFont: { size: 13 },
                callbacks: {
                  label: function(context) {
                    const label = context.label || '';
                    const value = context.raw || 0;
                    const percentage = toolData.percentages[context.dataIndex] || '0';
                    return `${label}: $${value.toFixed(2)} (${percentage}%)`;
                  }
                }
              }
            }
          }
        });
      }
    }

    // Cleanup on unmount
    return () => {
      if (barChartInstance.current) {
        barChartInstance.current.destroy();
      }
      if (pieChartInstance.current) {
        pieChartInstance.current.destroy();
      }
    };
  }, [creditUsages, t]);

  // Table columns for usage history
  const columns = [
    {
      title: t('date'),
      dataIndex: 'created_at',
      key: 'date',
      width: 120,
      render: (date) => {
        if (!date) return t('not_available');
        const dateObj = new Date(date);
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {dateObj.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })}
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
              {dateObj.toLocaleDateString('en-US', { year: 'numeric' })}
            </div>
          </div>
        );
      },
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend'
    },
    {
      title: t('tool'),
      dataIndex: 'tool_name',
      key: 'tool',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            backgroundColor: '#3b82f6' 
          }} />
          <span>{text || t('unknown_tool')}</span>
        </div>
      ),
      filters: Array.from(new Set(creditUsages.map(u => u.tool_name).filter(Boolean))).map(tool => ({ text: tool, value: tool })),
      onFilter: (value, record) => record.tool_name === value,
    },
    {
      title: t('cost'),
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      render: (cost) => (
        <Tag 
          color="red" 
          style={{ 
            fontWeight: 600, 
            borderRadius: '12px',
            margin: 0,
            border: 'none'
          }}
        >
          ${(cost || 0).toFixed(2)}
        </Tag>
      ),
      align: 'right',
      sorter: (a, b) => (a.cost || 0) - (b.cost || 0)
    }
  ];

  const costData = getCostOverTimeData();
  const toolData = getToolBreakdownData();

  const handleExportData = () => {
    toast.success(t('export_functionality_coming_soon'));
  };

  return (
    <div className="min-h-[70vh]">
      
      {/* Header Section */}
      <div className="mb-2">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
          <div>
            <Title level={2} style={{ marginBottom: 2, color: '#1f2937' }}>
              {t("dashboard_title")}
            </Title>
            <Text type="secondary" style={{ color: '#6b7280', fontSize: '13px' }}>
              {t("dashboard_subtitle")}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <StandardButton
              type="secondary"
              variant="outlined"
              onClick={handleExportData}
              icon={<DownloadOutlined />}
              size="small"
            >
              {t("export_data")}
            </StandardButton>
            <StandardButton
              type="default"
              onClick={handleLogout}
              icon={<LogoutOutlined />}
              size="small"
              danger
            >
              {t("logout_button")}
            </StandardButton>
          </div>
        </div>

        {/* Profile + Add Credits */}
        <Card className="rounded-xl shadow-sm border-0"
          style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
        >
          <div className="flex flex-row items-center justify-between gap-4 flex-nowrap">

            {/* Left Profile */}
            <div className="flex items-center space-x-3">
              <Badge dot color="#10b981" offset={[-5, 45]}>
                <Avatar
                  size={56}
                  src={userProfileData?.picture}
                  icon={!userProfileData?.picture && <UserOutlined />}
                  style={{
                    border: '3px solid rgba(255,255,255,0.3)',
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }}
                />
              </Badge>
              <div>
                <Title level={3} style={{ marginBottom: 2, color: 'white', fontSize: '18px' }}>
                  {userProfileData?.name || t('user')}
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                  {userProfileData?.email}
                </Text>
              </div>
            </div>

            {/* Balance */}
            <div className="bg-white/10 rounded-lg p-3 text-center min-w-[140px]">
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>
                {t("available_balance")}
              </div>
              <Title level={2} style={{ margin: 0, color: 'white', fontSize: '22px' }}>
                ${parseFloat(creditBalance || 0).toFixed(2)}
              </Title>
            </div>

            {/* Add Credits (Always Horizontal) */}
            <div className="bg-white/10 rounded-lg p-3 min-w-[320px]">
              <div className="flex items-center justify-between mb-2">
                <span style={{ color: 'white', fontWeight: 600, fontSize: '13px' }}>
                  {t("add_credits")}
                </span>
              </div>

              <div className="flex flex-nowrap gap-1 mb-2">
                {[
                  { amount: '5.00', label: '$5' },
                  { amount: '10.00', label: '$10' },
                  { amount: '20.00', label: '$20' },
                  { amount: '50.00', label: '$50' }
                ].map(item => (
                  <StandardButton
                    key={item.amount}
                    size="small"
                    onClick={() => setSelectedAmount(item.amount)}
                    style={{
                      flex: 1,
                      backgroundColor: selectedAmount === item.amount ? 'white' : 'rgba(255,255,255,0.1)',
                      color: selectedAmount === item.amount ? '#667eea' : 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                      fontSize: '12px',
                      minHeight: '28px'
                    }}
                  >
                    {item.label}
                  </StandardButton>
                ))}
              </div>

              <div className="text-xs text-white mb-2">
                {t("selected_amount")}: <strong>${selectedAmount}</strong>
              </div>

              <PayPalButtons
                style={{ layout: "horizontal", height: 30 }}
                createOrder={async () => {
                  const token = localStorage.getItem('authToken');
                  const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/create-order`, {
                    amount: selectedAmount,
                  }, {
                    headers: { 'Authorization': `Bearer ${token}` },
                  });
                  return res.data.id;
                }}
                onApprove={async (data) => {
                  const details = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/get-order`, {
                    orderID: data.orderID
                  });
                  if (details.data.status === "COMPLETED") {
                    const newBal = parseFloat(creditBalance) + parseFloat(selectedAmount);
                    localStorage.setItem("creditBalance", newBal.toString());
                    window.location.reload();
                  }
                }}
              />
            </div>

          </div>
        </Card>
      </div>

      {/* ===== MAIN GRID ===== */}
      <Row gutter={[12, 12]}>

        {/* LEFT COLUMN */}
        <Col xs={24} lg={16}>
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              {
                key: 'totalSpent',
                title: t("total_spend"),
                value: `$${kpiData.totalSpent}`,
                icon: <DollarOutlined />,
                suffix: t("all_time"),
                color: '#10b981',
                bgColor: '#d1fae5',
                trend: kpiData.monthlyTrend,
                subtext: `$${kpiData.thisMonthSpent} ${t("this_month")}`
              },
              {
                key: 'toolsUsed',
                title: t("tools_used"),
                value: kpiData.toolsUsed,
                icon: <ToolOutlined />,
                suffix: t("unique_tools"),
                color: '#3b82f6',
                bgColor: '#dbeafe',
                subtext: t("active_tools_count")
              },
              {
                key: 'mostUsedTool',
                title: t("top_tool"),
                value: kpiData.mostUsedTool,
                icon: <FireOutlined />,
                color: '#f59e0b',
                bgColor: '#fef3c7',
                subtext: t("most_frequently_used")
              },
              {
                key: 'lastUsed',
                title: t("last_activity"),
                value: kpiData.lastUsed,
                icon: <CalendarOutlined />,
                color: '#8b5cf6',
                bgColor: '#ede9fe',
                subtext: t("recent_usage_date")
              }
            ].map((item, index) => (
              <Card 
                key={index}
                className="rounded-xl hover:shadow transition-all duration-300"
                style={{ 
                  border: `1px solid #e5e7eb`,
                  height: '100%',
                  cursor: 'pointer'
                }}
                bodyStyle={{ padding: '8px' }}
              >
                <div className="flex items-center justify-between mb-1">
                  <div style={{ 
                    width: 28, 
                    height: 28, 
                    borderRadius: '6px',
                    backgroundColor: item.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(item.icon, { style: { fontSize: '14px', color: item.color } })}
                  </div>
                  {item.trend !== undefined && (
                    <div style={{ 
                      fontSize: '9px', 
                      fontWeight: 600,
                      color: item.trend >= 0 ? '#10b981' : '#ef4444',
                      backgroundColor: item.trend >= 0 ? '#d1fae5' : '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1px'
                    }}>
                      {item.trend >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      {Math.abs(item.trend)}%
                    </div>
                  )}
                </div>
                
                <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500, marginBottom: 2 }}>
                  {item.title}
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 700, 
                  color: '#1f2937',
                  lineHeight: '1.2',
                  marginBottom: '2px'
                }}>
                  {item.value}
                </div>
                
                <div className="flex items-center justify-between">
                  <Text type="secondary" style={{ fontSize: '9px' }}>
                    {item.subtext}
                  </Text>
                  {item.suffix && (
                    <Tag 
                      color="default" 
                      style={{ 
                        fontSize: '8px', 
                        padding: '0 3px',
                        margin: 0,
                        backgroundColor: '#f3f4f6'
                      }}
                    >
                      {item.suffix}
                    </Tag>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {/* Charts (NO EXTRA TOP MARGIN) */}
          <Card 
            className="rounded-xl shadow-sm"
            style={{ border: 0 }}
            bodyStyle={{ padding: '12px' }}
            title={
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div style={{ width: 3, height: 16, backgroundColor: '#3b82f6', borderRadius: 2, marginRight: 6 }}></div>
                  <span style={{ color: '#374151', fontWeight: 600, fontSize: '14px' }}>{t("spending_overview")}</span>
                </div>
                <Select defaultValue="7days" size="small" style={{ width: 100, fontSize: '12px' }}>
                  <Option value="7days">{t("last_7_days")}</Option>
                  <Option value="30days">{t("last_30_days")}</Option>
                  <Option value="90days">{t("last_90_days")}</Option>
                </Select>
              </div>
            }
          >
            {costData.labels.length > 0 ? (
              <div style={{ position: 'relative', height: '200px' }}>
                <canvas ref={barChartRef}></canvas>
              </div>
            ) : (
              <div style={{ 
                height: 150, 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center', 
                color: '#9ca3af',
                gap: '8px'
              }}>
                <DollarOutlined style={{ fontSize: '32px', opacity: 0.3 }} />
                <Text style={{ fontSize: '12px' }}>{t("no_spending_data")}</Text>
                <Text type="secondary" style={{ fontSize: '10px' }}>
                  {t("no_spending_data_subtitle")}
                </Text>
              </div>
            )}
          </Card>
        </Col>

        {/* RIGHT COLUMN */}
        <Col xs={24} lg={8}>
          {/* Tools Breakdown - Takes full height */}
          <Card 
            className="rounded-xl shadow-sm"
            style={{ border: 0, height: '100%' }}
            bodyStyle={{ padding: '12px', height: '100%', display: 'flex', flexDirection: 'column' }}
            title={
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div style={{ width: 3, height: 16, backgroundColor: '#f59e0b', borderRadius: 2, marginRight: 6 }}></div>
                  <span style={{ color: '#374151', fontWeight: 600, fontSize: '14px' }}>{t("tools_breakdown")}</span>
                </div>
                <Tag color="orange" style={{ fontSize: '10px' }}>{toolData.labels.length} {t("tools")}</Tag>
              </div>
            }
          >
            {toolData.labels.length > 0 ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ position: 'relative', height: '180px', minHeight: '180px', marginBottom: '8px' }}>
                  <canvas ref={pieChartRef}></canvas>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <div 
                  className="space-y-1" 
                  style={{ 
                    flex: 1, 
                    overflowY: 'auto', 
                    maxHeight: '200px',
                    paddingRight: '4px'
                  }}
                >
                  {toolData.labels.map((label, index) => (
                    <div key={index} className="flex items-center justify-between p-1 hover:bg-gray-50 rounded transition-colors">
                      <div className="flex items-center gap-1" style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          width: 8,
                          height: 8,
                          borderRadius: '2px',
                          backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#84cc16', '#f97316'][index] || '#9ca3af',
                          flexShrink: 0
                        }} />
                        <Text 
                          style={{ 
                            fontSize: '11px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '120px'
                          }}
                        >
                          {label}
                        </Text>
                      </div>
                      <div className="text-right" style={{ flexShrink: 0 }}>
                        <Text strong style={{ fontSize: '11px' }}>${toolData.data[index].toFixed(2)}</Text>
                        <Text type="secondary" style={{ fontSize: '9px', display: 'block' }}>
                          {toolData.percentages[index]}%
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center', 
                color: '#9ca3af',
                gap: '8px'
              }}>
                <ToolOutlined style={{ fontSize: '32px', opacity: 0.3 }} />
                <Text style={{ fontSize: '12px' }}>{t("no_tool_usage_data")}</Text>
                <Text type="secondary" style={{ fontSize: '10px' }}>
                  {t("no_tool_usage_data_subtitle")}
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== USAGE HISTORY (NO SPACE ABOVE) ===== */}
      <Card 
        className="rounded-xl shadow-sm"
        style={{ border: 0 }}
        bodyStyle={{ padding: '12px' }}
        title={
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div style={{ width: 3, height: 16, backgroundColor: '#6b7280', borderRadius: 2, marginRight: 6 }}></div>
              <span style={{ color: '#374151', fontWeight: 600, fontSize: '14px' }}>{t("usage_history")}</span>
            </div>
            <div className="flex items-center gap-1">
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {t("total_records")}: {creditUsages.length}
              </Text>
              <StandardButton
                type="link"
                size="small"
                onClick={handleExportData}
                icon={<DownloadOutlined />}
                style={{ fontSize: '11px' }}
              >
                {t("export_data")}
              </StandardButton>
            </div>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={creditUsages || []}
          rowKey={(record, index) => `${record.created_at}_${record.tool_name}_${index}`}
          pagination={{ 
            pageSize: 8,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${t('total')} ${total} ${t('records')}`,
            size: 'small',
            simple: true
          }}
          size="small"
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <div style={{ padding: '24px 0', textAlign: 'center' }}>
                <HistoryOutlined style={{ fontSize: '28px', color: '#d1d5db', marginBottom: '8px' }} />
                <div style={{ color: '#9ca3af', marginBottom: '4px', fontSize: '12px' }}>{t("no_usage_history")}</div>
                <Text type="secondary" style={{ fontSize: '10px' }}>
                  {t("no_usage_history_subtitle")}
                </Text>
              </div>
            )
          }}
        />
      </Card>

    </div>
  );
};

const LoginContent = () => {
  const [userProfileData, setUserProfileData] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditUsages, setCreditUsages] = useState([]);
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState('5.00');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');
  const [isLoading, setIsLoading] = useState(true);
  const [payPalInitialized, setPayPalInitialized] = useState(false);

  // LinkedIn OAuth
  const handleLinkedInLogin = () => {
    try {
      const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem('linkedin_oauth_state', state);

      const redirectUri = process.env.REACT_APP_LINKEDIN_REDIRECT_URI;
      const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
      const scope = encodeURIComponent('openid profile email');

      const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${state}`;
      window.location.href = linkedInAuthUrl;
    } catch (error) {
      toast.error(t('login_failed'));
    }
  };

  // Check localStorage for existing user
  useEffect(() => {
    const checkLocalStorage = () => {
      const token = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('userProfileData');
      if (token && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUserProfileData(parsedUser);

          const storedCredits = localStorage.getItem('creditBalance');
          if (storedCredits) {
            setCreditBalance(parseFloat(storedCredits));
          }
        } catch (_) {
        }
      }
      setIsLoading(false);
    };
    checkLocalStorage();
  }, []);

  // Handle LinkedIn backend redirect (receives JWT token in URL)
  useEffect(() => {
    const handleBackendRedirect = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const linkedinSuccess = urlParams.get('linkedin_success');
      const token = urlParams.get('token');
      const picture = urlParams.get('picture');
      const error = urlParams.get('error');

      if (linkedinSuccess === '1' && token) {
        try {
          // Store the JWT token
          localStorage.setItem('authToken', token);

          // Fetch user profile using the token
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user-profile`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });

          if (response?.data?.user) {
            const safeUserData = {
              ...response.data.user,
              picture: picture ? decodeURIComponent(picture) : null,
            };

            setUserProfileData(safeUserData);
            setCreditBalance(response.data.user.credits || 0);
            localStorage.setItem('userProfileData', JSON.stringify(safeUserData));
            localStorage.setItem('creditBalance', response.data.user.credits || 0);
            toast.success(t('linkedin_login_successful'));

            const redirectUrl = searchParams.get('redirect');
            if (redirectUrl) {
              navigate(redirectUrl);
            } else {
              navigate('/en');
            }
          }
        } catch (err) {
          localStorage.removeItem('authToken');
          toast.error(t('failed_to_process_login'));
        }
      } else if (error) {
        toast.error(error || t('linkedin_login_failed'));
      }
    };
    handleBackendRedirect();
  }, [navigate, searchParams, t]);

  // Get tool info from redirect
  const getToolInfoFromRedirect = (encodedPath) => {
    try {
      const decoded = decodeURIComponent(encodedPath);
      // Strip locale prefix and any trailing slash so we can match against
      // canonical tool paths like "/tools/multi-model-playground".
      const normalized = decoded
        .replace(/^\/(en|es|zh-CN|de|fr|ja)(?=\/|$)/, '')
        .replace(/\/+$/, '') || '/';

      for (const category of Object.values(groupedTools)) {
        for (const tool of category) {
          if (tool.path === normalized) return tool;
        }
      }
    } catch (_) {
    }
    return null;
  };
  
  const toolInfo = redirectPath ? getToolInfoFromRedirect(redirectPath) : null;

  // PayPal options
  const initialOptions = {
    'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID,
    currency: 'USD',
  };

  // Fetch user profile
  useEffect(() => {
    const fetchLatestUserProfile = async () => {
      if (!userProfileData) return;
      const token = localStorage.getItem('authToken');
      if (!token) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user-profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response?.data?.user) {
          const updatedUser = {
            ...userProfileData,
            ...response.data.user
          };
          setUserProfileData(updatedUser);
          setCreditBalance(response.data.user.credits || 0);
          localStorage.setItem('creditBalance', response.data.user.credits || 0);
        }
      } catch (_) {
      }
    };
    if (userProfileData?.id) {
      fetchLatestUserProfile();
    }
  }, [userProfileData?.id]);

  // Fetch usage data
  useEffect(() => {
    const fetchUsageData = async () => {
      if (!userProfileData?.id) return;
      const token = localStorage.getItem('authToken');
      if (!token) return;
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user-credits-usage`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setCreditUsages(res.data.usages || []);
      } catch (_) {
      }
    };
    if (userProfileData?.id) {
      fetchUsageData();
    }
  }, [userProfileData?.id]);

  // Google login
  const handleGoogleLoginSuccess = async (successResponse) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/google_login`, {
        access_token: successResponse.access_token,
      });
      if (response?.data?.token && response?.data?.user) {
        // Store JWT token
        localStorage.setItem('authToken', response.data.token);

        const safeUserData = {
          google_id: response.data.user.google_id,
          id: response.data.user.id,
          name: response.data.user.name,
          email: response.data.user.email,
          picture: response.data.user.picture,
        };
        setUserProfileData(safeUserData);
        setCreditBalance(response.data.user.credits || 0);
        localStorage.setItem('userProfileData', JSON.stringify(safeUserData));
        localStorage.setItem('creditBalance', response.data.user.credits || 0);
        const redirectUrl = searchParams.get('redirect');
        if (redirectUrl) navigate(redirectUrl); else navigate('/');
      } else {
        toast.error(response?.data?.error || t("login_failed"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || t("login_failed"));
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: handleGoogleLoginSuccess,
    onError: () => toast.error(t("login_failed")),
  });

  // Logout
  const handleLogout = () => {
    setUserProfileData(null);
    setCreditBalance(0);
    setCreditUsages([]);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfileData');
    localStorage.removeItem('creditBalance');
    navigate(`/${searchParams.get('lang') || 'en'}/auth/login`);
  };

  // Show loading spinner
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-0">
        <Header customHeading={t("account_center_title")} />
        <div className="max-w-5xl mx-auto">
          <Card className="p-6 rounded-xl shadow-lg">
            <div className="min-h-[55vh] flex items-center justify-center">
              <Spin size="large" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Check if user is logged in
  const isUserLoggedIn = !!localStorage.getItem('authToken') && !!userProfileData;

  return (
    <div className="min-h-screen bg-gray-50 p-0">
      <Header customHeading={t("account_center_title")} />
      <div className="max-w-7xl mx-auto">
        <Card className="p-4 rounded-xl shadow-lg">
          {!isUserLoggedIn ? (
            <div className="min-h-[55vh] flex flex-col items-center justify-center space-y-6">
              <Row justify="center" align="middle">
                <Col style={{ display: "flex", overflow: "hidden" }}>
                  {/* Login Form */}
                  <div style={{ flex: 1, padding: "40px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderRight: "1px solid #f0f0f0" }}>
                    <Title level={3} style={{ marginBottom: 30 }} className="text-indigo-600">
                      {t("signin")}
                    </Title>
                    <StandardButton 
                      type="secondary"
                      onClick={handleGoogleLogin} 
                      size="large" 
                      className="flex items-center justify-center gap-4 w-full"
                    >
                      <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google Logo" className="w-6 h-6" />
                      <span className="text-gray-700 font-medium text-base">{t("signin_with_google")}</span>
                    </StandardButton>
                    <StandardButton 
                      type="primary"
                      onClick={handleLinkedInLogin} 
                      size="large" 
                      className="flex items-center justify-center gap-4 w-full mt-4"
                      style={{ backgroundColor: '#0077b5', borderColor: '#0077b5' }}
                    >
                      <img src="https://content.linkedin.com/content/dam/me/business/en-us/amp/brand-site/v2/bg/LI-Bug.svg.original.svg" alt="LinkedIn Logo" className="w-6 h-6 filter brightness-0 invert" />
                      <span className="text-white font-medium text-base">{t("signin_with_linkedin")}</span>
                    </StandardButton>
                    <div className="mt-4 text-center">
                      <span className="text-sm">{t("dont_have_account")}{" "}
                        <StandardButton 
                          type="link"
                          onClick={() => navigate('/auth/register')} 
                          className="text-indigo-600 font-medium p-0"
                        >
                          {t("signup")}
                        </StandardButton>
                      </span>
                    </div>
                    <Text style={{ fontSize: 12, textAlign: "center", marginTop: 48 }}>
                      <Trans i18nKey="terms_and_privacy">
                        By signing in, you agree to our <a href="/en/terms/" className="text-indigo-600">Terms of Service</a> and <a href="/en/privacy/" className="text-indigo-600">Privacy Policy</a>.
                      </Trans>
                    </Text>
                  </div>

                  {/* Tool Info */}
                  <div style={{ flex: 1, padding: "40px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    {toolInfo?.icon ? <div className="flex items-center justify-center"><img src={toolInfo.icon} alt={t(toolInfo.nameKey)} className="w-20 h-20 rounded" loading="lazy" /></div> : ''}
                    <Title level={3}>{toolInfo?.nameKey ? t(toolInfo.nameKey) : t("home_site_title")}</Title>
                    <Text style={{ fontSize: 15, color: "#555" }}>{toolInfo?.descriptionKey ? t(toolInfo.descriptionKey) : t("quto_description")}</Text>
                    <br />
                    <Text style={{ fontSize: 15, color: "#555", fontWeight: 'bold' }}>{t("login_credit_message")}</Text>
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <PayPalScriptProvider options={initialOptions}>
              <AnalyticsDashboard 
                userProfileData={userProfileData}
                creditBalance={creditBalance}
                creditUsages={creditUsages}
                handleLogout={handleLogout}
                selectedAmount={selectedAmount}
                setSelectedAmount={setSelectedAmount}
              />
            </PayPalScriptProvider>
          )}
        </Card>
        <Footer />
      </div>
    </div>
  );
};

const Login = () => (
  <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID}>
    <LoginContent />
  </GoogleOAuthProvider>
);

export default Login;